import {useState, useCallback, useEffect} from 'react';

// La clé utilisée pour stocker l'ID de session dans localStorage
export const SESSION_ID_KEY = 'trace_session_id';

/**
 * Hook personnalisé pour gérer l'ID de session dans localStorage
 * @returns [sessionId, setSessionId, clearSessionId]
 */
export const useSessionId = (): [
  string | null,
  (id: string | null) => void,
  () => void,
] => {
  // Initialiser l'état avec la valeur de localStorage ou null
  const [sessionId, setInternalSessionId] = useState<string | null>(() => {
    // Seulement exécuté lors du premier rendu
    if (typeof window !== 'undefined') {
      return localStorage.getItem(SESSION_ID_KEY);
    }
    return null;
  });

  // Fonction pour mettre à jour l'ID de session dans l'état et localStorage
  const setSessionId = useCallback((id: string | null) => {
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem(SESSION_ID_KEY, id);
        console.log('Session ID saved to localStorage:', id);
      } else {
        localStorage.removeItem(SESSION_ID_KEY);
        console.log('Session ID removed from localStorage');
      }
    }
    setInternalSessionId(id);
  }, []);

  // Fonction pour effacer l'ID de session
  const clearSessionId = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_ID_KEY);
      console.log('Session ID cleared from localStorage');
    }
    setInternalSessionId(null);
  }, []);

  // Synchroniser l'état avec localStorage si changé ailleurs
  useEffect(() => {
    const storedSessionId = localStorage.getItem(SESSION_ID_KEY);
    if (sessionId !== storedSessionId) {
      setInternalSessionId(storedSessionId);
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SESSION_ID_KEY) {
        const newValue = event.newValue;
        console.log('Session ID changed in another tab:', newValue);
        setInternalSessionId(newValue);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange);
      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, []);

  return [sessionId, setSessionId, clearSessionId];
};
